import { useEffect, useState } from "react";
import { ArrowLeft, X } from "lucide-react";

import {
  CONTEXT_LAYER_META,
  formatPropValue,
  pickTitle,
  type ContextFillLayerId,
} from "../map/context-layer-meta";
import type { DeforestationLayerStates } from "../types/deforestation";

export interface FeatureHit {
  fillLayerId: ContextFillLayerId;
  feature: GeoJSON.Feature;
}

interface FeatureInfoPopupProps {
  position: { x: number; y: number };
  hits: FeatureHit[];
  layers: DeforestationLayerStates;
  onClose: () => void;
}

export function FeatureInfoPopup({
  position,
  hits,
  layers,
  onClose,
}: FeatureInfoPopupProps) {
  // If a single feature was hit, open straight on the detail table.
  // Otherwise show the list and let the user pick.
  const [selectedIdx, setSelectedIdx] = useState<number | null>(
    hits.length === 1 ? 0 : null,
  );

  useEffect(() => {
    setSelectedIdx(hits.length === 1 ? 0 : null);
  }, [hits]);

  const selected = selectedIdx !== null ? hits[selectedIdx] : null;

  return (
    <div
      className="absolute z-30"
      style={{ left: position.x, top: position.y }}
    >
      <div className="-translate-x-1/2 translate-y-3 w-[300px] max-h-[360px] overflow-hidden rounded-lg border border-border bg-card shadow-lg flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          {selected && hits.length > 1 ? (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setSelectedIdx(null)}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          ) : (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {hits.length} feature{hits.length === 1 ? "" : "s"} here
            </span>
          )}
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {selected ? (
            <DetailTable hit={selected} layers={layers} />
          ) : (
            <ul className="divide-y divide-border">
              {hits.map((h, i) => {
                const meta = CONTEXT_LAYER_META[h.fillLayerId];
                const color = layers[meta.stateKey].color;
                const title = pickTitle(h.feature, meta);
                return (
                  <li key={`${h.fillLayerId}-${i}`}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-accent/40 transition-colors flex items-start gap-2"
                      onClick={() => setSelectedIdx(i)}
                    >
                      <span
                        className="mt-1 inline-block h-2 w-2 rounded-full shrink-0"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                          {meta.label}
                        </span>
                        <span className="block truncate text-sm font-medium">
                          {title}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

interface DetailTableProps {
  hit: FeatureHit;
  layers: DeforestationLayerStates;
}

function DetailTable({ hit, layers }: DetailTableProps) {
  const meta = CONTEXT_LAYER_META[hit.fillLayerId];
  const color = layers[meta.stateKey].color;
  const title = pickTitle(hit.feature, meta);
  const props = (hit.feature.properties ?? {}) as Record<string, unknown>;
  // Drive rows from the curated whitelist (in its declared order); skip any
  // prop the feature doesn't carry rather than dumping every raw attribute.
  const rows = meta.detailProps
    .map((d) => ({ key: d.key, label: d.label, value: props[d.key] }))
    .filter((r) => r.value != null);

  return (
    <div className="px-3 py-2">
      <div className="flex items-start gap-2 pb-2">
        <span
          className="mt-1 inline-block h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {meta.label}
          </div>
          <div className="text-sm font-medium leading-tight">{title}</div>
        </div>
      </div>
      {rows.length > 0 ? (
        <table className="w-full text-xs border-t border-border">
          <tbody>
            {rows.map(({ key, label, value }) => (
              <tr key={key} className="border-b border-border last:border-b-0">
                <td className="py-1.5 pr-2 text-muted-foreground align-top w-[40%] break-words">
                  {label}
                </td>
                <td className="py-1.5 align-top break-words">
                  {formatPropValue(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-xs text-muted-foreground border-t border-border pt-2">
          No additional properties.
        </div>
      )}
    </div>
  );
}
